



































































































































































































































































































































import { Component, Prop, Vue } from "vue-property-decorator";
import { Department } from "@/typings";
import { shortSemToLongSem } from "@/utilities";

interface GearSpec {
  teeth: number;
  // Direction from the previous gear's center to this one, in degrees (ignored for the first gear)
  toward: number;
  metal: "brass" | "copper" | "steel";
}

interface Gear {
  x: number;
  y: number;
  teeth: number;
  metal: string;
  path: string;
  hub: number;
  bolts: { i: number; x: number; y: number }[];
  // Angle = phase + ratio * the driving angle, in degrees
  phase: number;
  ratio: number;
  angle: number;
}

interface Train {
  name: string;
  gears: Gear[];
}

// A tick on a gauge dial: the line from (x1, y1) to (x2, y2), and where its label goes
interface TickLine {
  x1: string;
  y1: string;
  x2: string;
  y2: string;
  lx: string;
  ly: string;
}

interface CreditTick extends TickLine {
  v: number;
  major: boolean;
}

interface ScheduleTick extends TickLine {
  k: number;
  major: boolean;
  label: string;
}

interface ClockTick {
  i: number;
  major: boolean;
  x1: string;
  y1: string;
  x2: string;
  y2: string;
}

const MODULE = 5.2; // tooth size: the pitch diameter is MODULE * teeth
const TRAIN_WIDTH = 190;

// A spur gear outline centered on 0,0, with spoke windows and a bore cut out (fill-rule evenodd)
function gearPath(teeth: number, m: number): string {
  const r = (m * teeth) / 2;
  const tip = r + m;
  const root = r - 1.25 * m;
  const pitch = (2 * Math.PI) / teeth;
  const pt = (rad: number, a: number) =>
    `${(rad * Math.cos(a)).toFixed(2)} ${(rad * Math.sin(a)).toFixed(2)}`;
  let d = "";
  for (let k = 0; k < teeth; k++) {
    const c = k * pitch;
    const points = [
      [root, c - 0.5 * pitch],
      [root, c - 0.27 * pitch],
      [tip, c - 0.14 * pitch],
      [tip, c + 0.14 * pitch],
      [root, c + 0.27 * pitch],
    ];
    for (const [rad, a] of points) {
      d += (d ? " L " : "M ") + pt(rad, a);
    }
  }
  d += " Z";
  const outer = root - 1.8 * m;
  const inner = Math.max(r * 0.34, 2.6 * m);
  const windows = teeth >= 22 ? 6 : teeth >= 15 ? 5 : 0;
  for (let w = 0; w < windows; w++) {
    const span = ((2 * Math.PI) / windows) * 0.64;
    const a0 = (w * 2 * Math.PI) / windows + 0.3;
    const a1 = a0 + span;
    d +=
      ` M ${pt(outer, a0)} A ${outer} ${outer} 0 0 1 ${pt(outer, a1)}` +
      ` L ${pt(inner, a1)} A ${inner} ${inner} 0 0 0 ${pt(inner, a0)} Z`;
  }
  const bore = 1.1 * m;
  d += ` M ${bore} 0 A ${bore} ${bore} 0 1 0 ${-bore} 0 A ${bore} ${bore} 0 1 0 ${bore} 0 Z`;
  return d;
}

// Lays out a train in which each gear meshes with the one before it, and works out each gear's
// phase so its teeth sit in the gaps of its neighbor
function buildTrain(
  start: { x: number; y: number },
  specs: GearSpec[]
): Gear[] {
  const gears: Gear[] = [];
  specs.forEach((spec, i) => {
    const r = (MODULE * spec.teeth) / 2;
    let x = start.x;
    let y = start.y;
    let phase = 0;
    let ratio = 1;
    if (i > 0) {
      const prev = gears[i - 1];
      const prevR = (MODULE * prev.teeth) / 2;
      const phi = (spec.toward * Math.PI) / 180;
      x = prev.x + (prevR + r) * Math.cos(phi);
      y = prev.y + (prevR + r) * Math.sin(phi);
      const prevPhase = (prev.phase * Math.PI) / 180;
      phase =
        phi +
        Math.PI +
        (prev.teeth / spec.teeth) * (phi - prevPhase) -
        Math.PI / spec.teeth;
      phase = (phase * 180) / Math.PI;
      ratio = (-prev.ratio * prev.teeth) / spec.teeth;
    }
    const hubR = Math.max(r * 0.34, 2.6 * MODULE) - 1;
    const boltCount = spec.teeth >= 22 ? 6 : 4;
    const boltR = 1.1 * MODULE + 5;
    gears.push({
      x,
      y,
      teeth: spec.teeth,
      metal: spec.metal,
      path: gearPath(spec.teeth, MODULE),
      hub: hubR,
      bolts: Array.from({ length: boltCount }, (_, b) => ({
        i: b,
        x: boltR * Math.cos((b * 2 * Math.PI) / boltCount),
        y: boltR * Math.sin((b * 2 * Math.PI) / boltCount),
      })),
      phase,
      ratio,
      angle: phase,
    });
  });
  return gears;
}

const LEFT: GearSpec[] = [
  { teeth: 32, toward: 0, metal: "brass" },
  { teeth: 14, toward: 58, metal: "copper" },
  { teeth: 24, toward: 118, metal: "brass" },
  { teeth: 12, toward: 40, metal: "steel" },
  { teeth: 36, toward: 112, metal: "brass" },
  { teeth: 16, toward: 50, metal: "copper" },
];
// The right train is laid out as if it were on the left, then mirrored by CSS
const RIGHT: GearSpec[] = [
  { teeth: 28, toward: 0, metal: "copper" },
  { teeth: 12, toward: 62, metal: "steel" },
  { teeth: 30, toward: 116, metal: "brass" },
  { teeth: 14, toward: 46, metal: "copper" },
  { teeth: 26, toward: 120, metal: "brass" },
];

// Engine-room decorations for the Steampunk theme: steam from the navbar's pipe, a panel of live
// gauges above the footer, and gear trains in the margins that mesh and turn as you scroll.
@Component
export default class SteampunkDecor extends Vue {
  @Prop({ required: true }) readonly place!: string;

  vents = ["sp-vent-a", "sp-vent-b"];
  trainWidth = TRAIN_WIDTH;
  viewportHeight = window.innerHeight;
  trains: Train[] = [
    { name: "left", gears: buildTrain({ x: -24, y: 240 }, LEFT) },
    { name: "right", gears: buildTrain({ x: -16, y: 300 }, RIGHT) },
  ];
  frame = 0;
  startTime = 0;
  clockStart = new Date();

  // ---------- Gauges ----------

  get selectedCrns(): Set<number> {
    const schedule = this.$store.state.schedule;
    const set = schedule.courseSets[schedule.currentTerm]?.[
      schedule.currentCourseSet
    ] as { [crn: string]: boolean } | undefined;
    return new Set(
      set
        ? Object.keys(set)
            .filter((crn) => set[crn])
            .map(Number)
        : []
    );
  }

  get credits(): number {
    const selected = this.selectedCrns;
    if (selected.size === 0) {
      return 0;
    }
    let total = 0;
    for (const dept of this.$store.state.departments as Department[]) {
      for (const course of dept.courses) {
        if (course.sections.some((section) => selected.has(section.crn))) {
          total += Math.min(...course.sections.map((s) => s.credMin));
        }
      }
    }
    return total;
  }

  get schedules(): number {
    return this.$store.getters["schedule/numSchedules"] ?? 0;
  }

  get semester(): string {
    return shortSemToLongSem()(process.env.VUE_APP_CURR_SEM);
  }

  // Dials sweep 270 degrees, from -135 (lower left) to 135 (lower right)
  dialAngle(fraction: number): number {
    return -135 + 270 * Math.min(1, Math.max(0, fraction));
  }

  get creditAngle(): number {
    return this.dialAngle(this.credits / 24);
  }

  get scheduleAngle(): number {
    return this.dialAngle(Math.log10(Math.max(1, this.schedules)) / 3);
  }

  polar(deg: number, r: number): { x: number; y: number } {
    const a = ((deg - 90) * Math.PI) / 180;
    return { x: r * Math.cos(a), y: r * Math.sin(a) };
  }

  // An arc along the dial between two values on a 0..max scale
  arc(from: number, to: number, max: number, r: number): string {
    const a = this.polar(this.dialAngle(from / max), r);
    const b = this.polar(this.dialAngle(to / max), r);
    const large = ((to - from) / max) * 270 > 180 ? 1 : 0;
    return `M ${a.x.toFixed(2)} ${a.y.toFixed(
      2
    )} A ${r} ${r} 0 ${large} 1 ${b.x.toFixed(2)} ${b.y.toFixed(2)}`;
  }

  tick(deg: number, major: boolean, inner: number): TickLine {
    const o = this.polar(deg, 47);
    const i = this.polar(deg, major ? inner - 3 : inner);
    const l = this.polar(deg, inner - 11);
    return {
      x1: o.x.toFixed(2),
      y1: o.y.toFixed(2),
      x2: i.x.toFixed(2),
      y2: i.y.toFixed(2),
      lx: l.x.toFixed(2),
      ly: l.y.toFixed(2),
    };
  }

  get creditTicks(): CreditTick[] {
    return Array.from({ length: 25 }, (_, v) => ({
      v,
      major: v % 4 === 0,
      ...this.tick(this.dialAngle(v / 24), v % 4 === 0, 42),
    }));
  }

  get majorCreditTicks(): CreditTick[] {
    return this.creditTicks.filter((t) => t.major);
  }

  get majorScheduleTicks(): ScheduleTick[] {
    return this.scheduleTicks.filter((t) => t.major);
  }

  get scheduleTicks(): ScheduleTick[] {
    const ticks: ScheduleTick[] = [];
    for (let decade = 0; decade <= 3; decade++) {
      for (let k = 1; k <= (decade === 3 ? 1 : 9); k++) {
        const value = k * 10 ** decade;
        const major = k === 1;
        ticks.push({
          k: value,
          major,
          label: value >= 1000 ? "1k" : String(value),
          ...this.tick(this.dialAngle(Math.log10(value) / 3), major, 42),
        });
      }
    }
    return ticks;
  }

  get clockTicks(): ClockTick[] {
    return Array.from({ length: 60 }, (_, i) => {
      const o = this.polar(i * 6, 47);
      const inner = this.polar(i * 6, i % 5 === 0 ? 41 : 44);
      return {
        i,
        major: i % 5 === 0,
        x1: o.x.toFixed(2),
        y1: o.y.toFixed(2),
        x2: inner.x.toFixed(2),
        y2: inner.y.toFixed(2),
      };
    });
  }

  get numerals(): { label: string; x: string; y: string }[] {
    const labels = [
      "XII",
      "I",
      "II",
      "III",
      "IIII",
      "V",
      "VI",
      "VII",
      "VIII",
      "IX",
      "X",
      "XI",
    ];
    return labels.map((label, i) => {
      const p = this.polar(i * 30, 34);
      return { label, x: p.x.toFixed(2), y: p.y.toFixed(2) };
    });
  }

  get clockTitle(): string {
    return "Chronometer, showing the time when the page loaded and ticking on from there";
  }

  // Clock hands turn with CSS animations started at the current time, so no timer has to run
  handStyle(hand: "hour" | "minute" | "second"): { [key: string]: string } {
    const d = this.clockStart;
    const seconds = d.getSeconds() + d.getMilliseconds() / 1000;
    const minutes = d.getMinutes() + seconds / 60;
    const hours = (d.getHours() % 12) + minutes / 60;
    const elapsed = {
      second: seconds,
      minute: minutes * 60,
      hour: hours * 3600,
    }[hand];
    const angle = {
      second: seconds * 6,
      minute: minutes * 6,
      hour: hours * 30,
    }[hand];
    return {
      "--sp-start": `${angle}deg`,
      animationDelay: `-${elapsed.toFixed(2)}s`,
    };
  }

  // ---------- Gears ----------

  // The gears are only shown on wide layouts (see the CSS), so they only turn there
  wide = window.matchMedia("(min-width: 1200px)");
  reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  mounted(): void {
    if (this.place !== "overlay") {
      return;
    }
    window.addEventListener("resize", this.onResize);
    this.startTime = performance.now();
    this.updateLoop();
  }

  beforeDestroy(): void {
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    window.removeEventListener("resize", this.onResize);
  }

  onResize(): void {
    this.viewportHeight = window.innerHeight;
    this.updateLoop();
  }

  // Turns the gears only while they are visible and motion is allowed. With reduced motion they
  // stay still, scrolling included.
  updateLoop(): void {
    const run = this.wide.matches && !this.reduceMotion.matches;
    if (run && !this.frame) {
      this.frame = requestAnimationFrame(this.tickGears);
    } else if (!run && this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  }

  tickGears(now: number): void {
    this.setAngles((now - this.startTime) / 1000);
    this.frame = requestAnimationFrame(this.tickGears);
  }

  // The driving angle: a slow idle turn plus a full turn for every 1500px scrolled
  setAngles(seconds: number): void {
    const drive = seconds * 4 + (window.scrollY / 1500) * 360;
    for (const train of this.trains) {
      const refs = this.$refs[train.name + "-gear"] as
        | SVGGElement[]
        | undefined;
      train.gears.forEach((gear, i) => {
        const angle = gear.phase + gear.ratio * drive;
        refs?.[i]?.setAttribute(
          "transform",
          `translate(${gear.x} ${gear.y}) rotate(${angle.toFixed(2)})`
        );
      });
    }
  }
}
